import { Account } from '@/lib/redux/app.slice';

/**
 * Gets the parent chain for a given account, from root to the account itself.
 * Returns array of accounts in order: [rootAccount, parentAccount, ..., currentAccount]
 * Uses parent_account_key if available to ensure complete hierarchy.
 *
 * @param accountId - The account ID to get the parent chain for
 * @param allAccounts - All available accounts from Redux state
 * @returns Array of accounts from root to current, or empty array if account not found
 */
export const getParentChain = (
  accountId: string | null,
  allAccounts: Account[] | null
): Account[] => {
  if (!accountId || !allAccounts || allAccounts.length === 0) {
    return [];
  }

  const account = allAccounts.find((acc) => acc.account_id === accountId);
  if (!account) {
    return [];
  }

  // If parent_account_key is available, use it to build the chain
  if (account.parent_account_key) {
    const parentIds = parseParentAccountKey(account.parent_account_key);
    const chain: Account[] = [];

    // Add all parent accounts in order (from root to direct parent)
    for (let i = parentIds.length - 1; i >= 0; i--) {
      const parentId = parentIds[i];
      const parent = allAccounts.find((acc) => acc.account_id === parentId);
      if (parent) {
        chain.push(parent);
      }
    }

    // Add the current account at the end
    chain.push(account);
    return chain;
  }

  // Fallback: Build the chain by walking up the parent hierarchy
  const chain: Account[] = [account];
  let currentAccount = account;

  while (currentAccount.parent_account_id) {
    const parent = allAccounts.find(
      (acc) => acc.account_id === currentAccount.parent_account_id
    );

    if (!parent) {
      // Parent not found in allAccounts - chain is incomplete
      break;
    }

    chain.unshift(parent); // Add to beginning of array
    currentAccount = parent;
  }

  return chain;
};

/**
 * Determines if a user can switch to a specific account based on their permissions.
 *
 * Permission rules:
 * - Super users can switch to any account
 * - Other users can only switch to:
 *   - Accounts in their rootAccounts list
 *   - Descendants (sub-accounts) of accounts in their rootAccounts list
 *
 * @param targetAccountId - The account ID the user wants to switch to
 * @param allAccounts - All available accounts from Redux state
 * @param rootAccounts - User's accessible root account IDs
 * @param isSuper - Whether the user is a super user
 * @returns Boolean indicating if the user can switch to the target account
 */
export const canSwitchToAccount = (
  targetAccountId: string,
  allAccounts: Account[] | null,
  rootAccounts: string[] | null,
  isSuper: boolean
): boolean => {
  // Super users can access any account
  if (isSuper) {
    return true;
  }

  // Non-super users need rootAccounts
  if (!rootAccounts || rootAccounts.length === 0) {
    return false;
  }

  if (!allAccounts || allAccounts.length === 0) {
    return false;
  }

  const targetAccount = allAccounts.find((acc) => acc.account_id === targetAccountId);
  if (!targetAccount) {
    return false;
  }

  // Check if target account is in rootAccounts
  if (rootAccounts.includes(targetAccountId)) {
    return true;
  }

  // Check if target account is a descendant of any root account
  // Walk up the parent chain to see if we reach a root account
  let currentAccount = targetAccount;
  while (currentAccount.parent_account_id) {
    if (rootAccounts.includes(currentAccount.parent_account_id)) {
      return true;
    }

    const parent = allAccounts.find(
      (acc) => acc.account_id === currentAccount.parent_account_id
    );

    if (!parent) {
      // Parent not found - can't verify access
      break;
    }

    currentAccount = parent;
  }

  return false;
};

/**
 * Parses a parent_account_key string to extract all parent account IDs.
 * The key format is "root.parent.grandparent" read right-to-left.
 * Example: "11480092.11480025" means 11480025 is parent, 11480092 is grandparent.
 *
 * @param parentAccountKey - The parent account key string
 * @returns Array of parent account IDs in order from direct parent to root
 */
export const parseParentAccountKey = (parentAccountKey: string | undefined): string[] => {
  if (!parentAccountKey) return [];

  // Split by dot and reverse (since it's read right-to-left)
  const parts = parentAccountKey.split('.');
  return parts.reverse();
};

/**
 * Checks if a parent account chain is complete (all parents loaded).
 * A chain is incomplete if any account has parents in parent_account_key but those parents
 * are not found in allAccounts.
 *
 * @param accountId - The account ID to check
 * @param allAccounts - All available accounts from Redux state
 * @returns Object with isComplete boolean and missing parent IDs array
 */
export const isParentChainComplete = (
  accountId: string | null,
  allAccounts: Account[] | null
): { isComplete: boolean; missingParentIds: string[] } => {
  if (!accountId || !allAccounts) {
    return { isComplete: true, missingParentIds: [] };
  }

  const account = allAccounts.find((acc) => acc.account_id === accountId);
  if (!account) {
    return { isComplete: true, missingParentIds: [] };
  }

  const missingParentIds: string[] = [];

  // Use parent_account_key if available to get full hierarchy
  if (account.parent_account_key) {
    const parentIds = parseParentAccountKey(account.parent_account_key);

    for (const parentId of parentIds) {
      const parent = allAccounts.find((acc) => acc.account_id === parentId);
      if (!parent) {
        missingParentIds.push(parentId);
      }
    }
  } else if (account.parent_account_id) {
    // Fallback to parent_account_id if parent_account_key not available
    let currentAccount = account;

    while (currentAccount.parent_account_id) {
      const parent = allAccounts.find(
        (acc) => acc.account_id === currentAccount.parent_account_id
      );

      if (!parent) {
        missingParentIds.push(currentAccount.parent_account_id);
        break; // Can't continue checking beyond missing parent
      }

      currentAccount = parent;
    }
  }

  return {
    isComplete: missingParentIds.length === 0,
    missingParentIds,
  };
};
